import { useEffect, useRef, useState, useContext } from "react";
import { Flippy, FrontSide, BackSide } from "react-flippy";
import { Typography, Box } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CircleIcon from "@mui/icons-material/Circle";
import CancelIcon from "@mui/icons-material/Cancel";
import LoadingWheel from "../../../../common/LoadingWheel";
import { GlobalContext } from "../../../../common/Context";
import { useTranslation } from "react-i18next";
import {
  blue,
  boxShadow,
  green,
  grey,
  red,
  white,
} from "../../../../../theme/colours";
import { formatThousand } from "../../../../common/Utils/Utils";
import { STATS_CARD_BASE } from "../../../../../constants/ids";

const FlippyCard = ({
  isLoading,
  difference,
  iconSrc,
  title,
  frontDesc,
  backDesc,
  statLine,
  id,
}) => {
  const { t } = useTranslation("Stats");
  const { mobileView, tabletView, desktopView, userBrowserName } =
    useContext(GlobalContext);

  const [color, setColor] = useState(null);
  const [backType, setBackType] = useState(0);
  const [variant, setVariant] = useState();
  const [colorFlip, setColorFlip] = useState(false);
  const [colorMsg, setColorMsg] = useState("");

  const ref = useRef();

  const flippyStyle = {
    width: mobileView ? "94%" : tabletView ? "31%" : "32%",
    height: mobileView ? "85%" : "100%",
  };
  const background = {
    background: `linear-gradient(90deg, ${blue[3]}, ${green[3]})`,
    boxShadow: `0px 3px 6px ${boxShadow}`,
    borderRadius: "0.5rem",
  };

  const cardStyleF = {
    padding: !mobileView ? "5%" : 0,
    display: mobileView && "flex",
    opacity: colorFlip ? "0%" : "100%",
    ...background,
  };
  const cardStyleB = {
    boxShadow: "none",
    display: "flex",
    padding: !mobileView ? "5%" : 0,
    opacity: !colorFlip ? "0%" : "100%",
    ...background,
    ...(backType === 0 && { background: color }),
  };
  const cardStyleFF = {
    width: mobileView ? "94%" : tabletView ? "92%" : "32%",
    boxShadow: "none",
    padding: mobileView || tabletView ? "5%" : "2% 2% 1%",
    position: "relative",
  };
  const cardDetails = {
    display: "flex",
    alignItems: "center",
    position: "relative",
    flexDirection: tabletView && "column",
    width: "100%",
    p: mobileView && "10% 4%",
  };
  const infoIcon = {
    transform: "scale(0.75)",
    color: grey[1],
    ...(desktopView && {
      mt: "-10%",
      ml: "-2%",
    }),
    ...(!desktopView && {
      position: "absolute",
      left: "0%",
      top: "0%",
    }),
  };
  const iconStyle = {
    width: tabletView ? "1.5rem" : desktopView ? "2rem" : "auto",
    height: tabletView ? "1.5rem" : desktopView ? "2rem" : "auto",
    "& *": {
      fill: white,
    },
  };
  const iconBox = {
    display: "flex",
    justifyContent: "center",
    padding: tabletView ? "5%" : "3%",
    borderRadius: "50%",
    background: mobileView ? green[2] : green[1],
    ml: mobileView && "2.5%",
    width: mobileView ? "16%" : "auto",
    aspectRatio: "1/1",
    "& svg": {
      ...iconStyle,
    },
  };
  const descBox = {
    width: tabletView ? "auto" : "60%",
    m: tabletView ? "5% 0" : "0 5%",
    textAlign: tabletView && "center",
  };
  const backDescBox = {
    width: "100%",
  };
  const titleText = {
    fontWeight: 500,
    lineHeight: "1.5",
    ...(mobileView && {
      fontSize: "0.75rem",
    }),
  };
  const descText = {
    fontWeight: 300,
    ...(mobileView && {
      fontSize: "0.75rem",
    }),
  };
  const circleIcon = {
    position: "absolute",
    top: "-0.375em",
    right: "-0.375em",
    color: color,
    transform: "scale(1)",
  };
  const colorText = {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    width: "90%",
    left: "50%",
    top: "50%",
    textAlign: "center",
    color: color === red[1] && white,
  };
  const cancelIcon = {
    position: "absolute",
    top: 0,
    right: 0,
    transform: "scale(0.75)",
    color: white,
  };
  const loadBox = { height: "100%" };
  const statStyle = {
    fontWeight: 800,
    fontSize: mobileView ? "1.25rem" : "2.75rem",
    mr: mobileView && "3%",
  };

  useEffect(() => {
    let result = "";
    switch (title) {
      case t("avgDriverScore"):
        result = "Score";
        break;
      case t("myAvgEvents"):
        result = "Event";
        break;
      case t("tryps"):
        result = "Tryps";
        break;
    }
    setVariant(result);
  }, [t]);

  useEffect(() => {
    const getColor = () => {
      if (isLoading) {
        return "#e9bc84";
      }
      if (difference < 0) {
        setColorMsg(
          t(`flippyDown${variant}`, { amount: Math.abs(difference) }),
        );
        return red[1];
      }
      if (difference > 0) {
        setColorMsg(t(`flippyUp${variant}`, { amount: difference }));
        return green[1];
      }
      setColorMsg(t("flippySame"));
      return "#e9bc84";
    };
    difference !== undefined && setColor(getColor);
  }, [difference, isLoading, t]);

  const handleFlip = () => {
    ref.current.toggle();
    setColorFlip(false);
    setTimeout(() => {
      setBackType(0);
    }, 150);
  };

  const handleInfoFlip = () => {
    ref.current.toggle();
    setColorFlip(true);
    setTimeout(() => {
      setBackType(1);
    }, 150);
  };

  const handleColorFlip = () => {
    setColorFlip(true);
    ref.current.toggle();
  };

  return (
    <>
      {userBrowserName !== "Firefox" ? (
        <Flippy
          flipOnClick={false}
          flipDirection="vertical"
          ref={ref}
          style={flippyStyle}>
          <FrontSide style={cardStyleF}>
            <Box sx={cardDetails} {...(id && { id: STATS_CARD_BASE(id) })}>
              <InfoIcon sx={infoIcon} onClick={handleInfoFlip} />
              <Box sx={iconBox}>{iconSrc}</Box>
              <Box sx={descBox}>
                <Typography sx={titleText} variant="body2">
                  {title}
                </Typography>
                <Typography sx={descText} variant="body2">
                  {frontDesc}
                </Typography>
              </Box>
              {isLoading ? (
                <Box sx={loadBox}>
                  <LoadingWheel width={2} />
                </Box>
              ) : (
                <Typography sx={statStyle}>
                  {formatThousand(statLine)}
                </Typography>
              )}
            </Box>
            {!isLoading && color && (
              <CircleIcon sx={circleIcon} onClick={handleColorFlip} />
            )}
          </FrontSide>
          <BackSide style={cardStyleB}>
            {backType === 0 ? (
              <>
                {isLoading ? (
                  <Box sx={loadBox}>
                    <LoadingWheel width={2} />
                  </Box>
                ) : (
                  <Typography sx={colorText} variant="flippySubTitle">
                    {colorMsg}
                  </Typography>
                )}
                <CancelIcon sx={cancelIcon} onClick={handleFlip} />
              </>
            ) : (
              <>
                <Box sx={cardDetails}>
                  <InfoIcon sx={infoIcon} onClick={handleFlip} />
                  <Box sx={iconBox}>{iconSrc}</Box>
                  <Box sx={{ ...descBox, ...backDescBox }}>
                    <Typography sx={titleText} variant="body2">
                      {title}
                    </Typography>
                    <Typography sx={descText} variant="body2">
                      {backDesc}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </BackSide>
        </Flippy>
      ) : (
        <Box sx={cardStyleFF} {...(id && { id: STATS_CARD_BASE(id) })}>
          <Box sx={cardDetails}>
            <Box sx={iconBox}>{iconSrc}</Box>
            <Box sx={descBox}>
              <Typography sx={titleText} variant="body2">
                {title}
              </Typography>
              <Typography sx={descText} variant="body2">
                {frontDesc}
              </Typography>
            </Box>
            {isLoading ? (
              <Box sx={loadBox}>
                <LoadingWheel width={2} />
              </Box>
            ) : (
              <Typography variant="h1">{formatThousand(statLine)}</Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default FlippyCard;
